
import { Component, Vue } from 'vue-property-decorator';
import {
  WebsocketDialogMessage,
  WebsocketNotificationInterface,
  WebsocketRoleUpdatedMessage,
} from '@/services/WS/interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import { UserLoginPayloadWSMessage } from '@/services/Auth/Interfaces';
import GlobalJumpPalette from '@/components/global-palette/GlobalJumpPalette.vue';
import ShortcutsHelp from '@/components/global-palette/ShortcutsHelp.vue';
import { isJumpPaletteHotkey, isShortcutsHelpHotkey } from '@/components/global-palette/hotkeys';

@Component({
  components: {
    GlobalJumpPalette,
    ShortcutsHelp,
  },
  sockets: {
    async notification(notification: WebsocketNotificationInterface): Promise<void> {
      if (this.$store.getters.wsPopupsEnabled) {
        this.$notifications[notification.level](notification.title, notification.body);
      }
      this.$store.commit('SOCKET_notification', notification);
    },
    dialog(message: WebsocketDialogMessage): void {
      if (!this.$store.getters.wsPopupsEnabled) {
        this.$store.commit('SOCKET_notification', {
          _id: `ws-dialog-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          title: message.type === 'error' ? 'Error' : (message.type === 'info' ? 'Info' : 'Success'),
          body: message.message,
          level: message.type,
          date: new Date().toISOString(),
        });
        return;
      }

      switch (message.type) {
        case 'success':
          return this.$successDialog.show(message.message)
        case 'error':
          return this.$errorDialog.show(message.message)
        case 'info':
          return this.$infoDialog.show(message.message)
      }
    },
    async forceLogout(): Promise<void> {
      this.$store.commit('USER_LOGOUT');
      this.$socket.disconnect();
      await this.$router.push({ name: 'auth_login' });
      this.$notifications.info('FORCE LOGOUT', 'You have been logged out, please login again.');
    },
    async roleUpdated(message: WebsocketRoleUpdatedMessage): Promise<void> {
      this.$store.commit('UPDATE_SCOPES', message.scopes);
      this.$notifications.info('Your Permissions Has Been Updated');
    },
    async updateCredentials(message: UserLoginPayloadWSMessage): Promise<void> {
      this.$store.commit('USER_LOGOUT');
      this.$socket.io.opts.query = { key: message.jwt };
      message.id = message.user;
      this.$store.commit('USER_LOGIN', message);
      this.$socket.connect();
      this.$notifications.info('Your Role Has Been Changed');
      this.$emit('UPDATED_CREDENTIALS', message.id);
    },
  },
  inject: [
    'profileRepository',
  ],
})
export default class App extends Vue {
  private readonly profileRepository!: ProfileRepositoryInterface;

  public jumpPaletteOpen = false;
  public shortcutsHelpOpen = false;

  private boundKeydown: ((event: KeyboardEvent) => void) | null = null;

  get isLoggedIn(): boolean {
    return !!this.$store.state.user.jwt;
  }

  private async beforeCreate(): Promise<void> {
    if (localStorage.getItem('vuex') && JSON.parse(localStorage.getItem('vuex') as string).user.jwt !== undefined) {
      const { data } = await this.$http.get('/auth/validate');
      if (!data.valid) {
        this.$store.commit('USER_LOGOUT');
        await this.$router.push({ name: 'auth_login' });
      }
    }

    this.$vuetify.theme.dark = this.$store.getters.darkTheme;

    this.$on('UPDATED_CREDENTIALS', async (userId: string) => {
      const profile = await this.profileRepository.getBaseProfileDataByUserId(userId);
      if (profile) {
        this.$store.commit('SET_NAME', `${profile.firstName} ${profile.lastName}`);
      }
    });
  }

  private async created(): Promise<void> {
    if (!this.$store.getters.id) {
      return;
    }
  }

  public mounted(): void {
    this.boundKeydown = (event: KeyboardEvent) => this.onGlobalKeydown(event);
    // capture: true so we beat Chrome's Ctrl+K omnibox shortcut
    document.addEventListener('keydown', this.boundKeydown, true);
    this.$root.$on('open-global-palette', this.openJumpPalette);
    this.$root.$on('open-shortcuts-help', this.openShortcutsHelp);
  }

  public beforeDestroy(): void {
    if (this.boundKeydown) {
      document.removeEventListener('keydown', this.boundKeydown, true);
      this.boundKeydown = null;
    }
    this.$root.$off('open-global-palette', this.openJumpPalette);
    this.$root.$off('open-shortcuts-help', this.openShortcutsHelp);
  }

  public openJumpPalette(): void {
    if (!this.isLoggedIn) {
      return;
    }
    this.shortcutsHelpOpen = false;
    this.jumpPaletteOpen = true;
  }

  public openShortcutsHelp(): void {
    if (!this.isLoggedIn) {
      return;
    }
    this.jumpPaletteOpen = false;
    this.shortcutsHelpOpen = true;
  }

  private onGlobalKeydown(event: KeyboardEvent): void {
    if (!this.isLoggedIn) {
      return;
    }

    if (event.key === 'Escape') {
      if (this.shortcutsHelpOpen) {
        event.preventDefault();
        this.shortcutsHelpOpen = false;
        return;
      }
      if (this.jumpPaletteOpen) {
        event.preventDefault();
        this.jumpPaletteOpen = false;
        return;
      }
    }

    if (isJumpPaletteHotkey(event)) {
      event.preventDefault();
      event.stopPropagation();
      if (this.jumpPaletteOpen) {
        this.jumpPaletteOpen = false;
      } else {
        this.openJumpPalette();
      }
      return;
    }

    if (isShortcutsHelpHotkey(event)) {
      event.preventDefault();
      event.stopPropagation();
      if (this.shortcutsHelpOpen) {
        this.shortcutsHelpOpen = false;
      } else {
        this.openShortcutsHelp();
      }
    }
  }
}
